import { Controller } from '@hotwired/stimulus';
import * as bootstrap from 'bootstrap';

export default class extends Controller {
  open() {
    const offcanvasEl = this.element.querySelector('.offcanvas');
    if (offcanvasEl) {
      this.panel = new bootstrap.Offcanvas(offcanvasEl);
      this.panel.show();
      this.#pushHistoryEntry();
      this.#installDismissGuards(offcanvasEl, 'hide.bs.offcanvas');

      offcanvasEl.addEventListener('hidden.bs.offcanvas', () => {
        document.activeElement?.blur();
        this.#dispose();
      }, {once: true});
      return;
    }

    const modalEl = this.element.querySelector('.modal');
    if (modalEl) {
      this.panel = new bootstrap.Modal(modalEl);
      this.panel.show();
      this.#pushHistoryEntry();
      this.#installDismissGuards(modalEl, 'hide.bs.modal');

      modalEl.addEventListener('hidden.bs.modal', () => {
        document.activeElement?.blur();
        this.#dispose();
      }, {once: true});
    }
  }

  teardown() {
    if (!this.panel) return;

    document.activeElement?.blur();
    // Synchronous teardown for turbo:before-cache: Bootstrap's hide() is
    // transition-based, so the async `hidden` event would fire AFTER the
    // snapshot is captured — leaving a stale offcanvas in the cache. We
    // dispose immediately instead.
    this.forceHide = true;
    this.#dispose();
  }

  // Blocks backdrop dismiss while the visible modal-step target is flagged
  // `data-locked="true"` (e.g. Verification / Generation / Signature steps
  // in the investment funnel). Close button, Escape and programmatic hides
  // remain allowed via the forceHide flag.
  #installDismissGuards(rootEl, hideEventName) {
    this.boundHideGuard = (event) => {
      if (this.forceHide) {
        this.forceHide = false;
        return;
      }
      if (this.#isCurrentStepLocked(rootEl)) {
        event.preventDefault();
      }
    };
    rootEl.addEventListener(hideEventName, this.boundHideGuard);

    this.boundDismissClick = (event) => {
      if (event.target.closest('[data-bs-dismiss]')) {
        this.forceHide = true;
      }
    };
    rootEl.addEventListener('click', this.boundDismissClick, true);

    this.boundEscape = (event) => {
      if (event.key === 'Escape') this.forceHide = true;
    };
    rootEl.addEventListener('keydown', this.boundEscape);
  }

  #isCurrentStepLocked(rootEl) {
    const steps = rootEl.querySelectorAll('[data-modal-steps-target="step"]');
    if (steps.length === 0) return false;
    const visible = Array.from(steps).find((step) => (
      !step.classList.contains('d-none')
    ));
    return visible?.dataset.locked === 'true';
  }

  #releaseBodyScrollLock() {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    document.body.classList.remove('modal-open');
  }

  #pushHistoryEntry() {
    // Preserve Turbo's existing history state (restorationIdentifier, etc.)
    // so that a back navigation later still triggers a proper restore.
    const mergedState = {...(history.state || {}), frameModalOpen: true};
    history.pushState(mergedState, '', window.location.href);
    this.historyPushed = true;
    this.boundPopState = () => {
      if (this.suppressPopState) {
        this.suppressPopState = false;
        return;
      }
      if (!this.panel) return;

      this.closedByPopState = true;
      this.historyPushed = false;
      this.forceHide = true;
      this.panel.hide();
    };
    window.addEventListener('popstate', this.boundPopState);
  }

  #dispose() {
    const shouldRollbackHistory =
      this.historyPushed && !this.closedByPopState && !this.navigating;

    if (this.boundPopState) {
      window.removeEventListener('popstate', this.boundPopState);
      this.boundPopState = null;
    }

    if (this.panel) {
      this.panel.dispose();
      this.panel = null;
    }
    // Bootstrap's ScrollBarHelper locks the body via inline styles when the
    // offcanvas/modal is shown, and only releases them inside hide()'s async
    // completion callback. Disposing synchronously skips that — reset manually.
    this.#releaseBodyScrollLock();
    this.forceHide = false;
    this.boundHideGuard = null;
    this.boundDismissClick = null;
    this.boundEscape = null;
    this.element.innerHTML = '';
    this.element.removeAttribute('src');

    if (shouldRollbackHistory) {
      this.suppressPopState = true;
      history.back();
      this.historyPushed = false;
      this.closedByPopState = false;
      return;
    }

    this.historyPushed = false;
    const closedByPopState = this.closedByPopState;
    this.closedByPopState = false;

    // Only refresh current page if no other navigation
    // is in progress (e.g. user clicked a CTA link) and if
    // the closure wasn't already triggered by the browser back button.
    if (!this.navigating && !closedByPopState) {
      const url = new URL(window.location.href);
      url.searchParams.delete('finalize');
      url.searchParams.delete('invested');
      Turbo.visit(url.toString(), {action: 'replace'});
    }
  }

  connect() {
    this.navigating = false;
    this.historyPushed = false;
    this.closedByPopState = false;
    this.suppressPopState = false;
    this.forceHide = false;
    this.boundTrack = () => { this.navigating = true; };
    document.addEventListener('turbo:before-visit', this.boundTrack);
  }

  disconnect() {
    document.removeEventListener('turbo:before-visit', this.boundTrack);
    if (this.boundPopState) {
      window.removeEventListener('popstate', this.boundPopState);
      this.boundPopState = null;
    }

    // Fallback cleanup: if the controller is torn down before Bootstrap
    // has fired its async `hidden` event, `#dispose()` never ran — do the
    // minimum ourselves to avoid leaking the Bootstrap instance / DOM.
    if (this.panel) {
      this.panel.dispose();
      this.panel = null;
      this.#releaseBodyScrollLock();
      this.element.innerHTML = '';
      this.element.removeAttribute('src');
    }

    // If we still have a phantom history entry and we're NOT in the middle
    // of a Turbo visit (boundTrack already handles that case), roll it back.
    if (this.historyPushed && !this.navigating) {
      this.suppressPopState = true;
      this.historyPushed = false;
      history.back();
    }
  }
}
